import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { LocationResult, SlotType } from '../../../types/SchedulerTypes';

export interface Location {
	name: string;
	office: string;
	phone: string;
	distance: string;
	address: string;
	offerDateTimeUTC: string;
	siteId?: string;
	siteName?: string;
	zip?: string;
	timezone?: string;
	brand?: string;
	originalBrand?: string;
	revspringAccountId?: string;
	revspringLocationValue?: string;
	providerName?: string;
	currentBrand?: string;
}

export interface LocationState {
	location: string;
	status: 'default' | 'loading' | 'valid' | 'invalid';
	searchResult: LocationResult[] | null;
	selectedTag: string;
	selectedPhysicalTag: string;
	zipCode: string;
	apptType: string;
	selectedDate: string;
	accountIdError: boolean;
	offerDateTimeUTC: string;
	selectedLocation?: Location;
	isWalkIn: boolean;
	selectedFilter: string;
	isOpenNow: boolean;
	errors: { errorTitle: string; error: string; errorLink: string }[];
	isNextDateDisabled: boolean;
	searchCounter: number;
	timezone?: string;
	locationSlots: Record<string, SlotType[]>;
	setTimezone: (timezone: string) => void;
	setIsWalkin: (isWalkIn: boolean) => void;
	setSelectedLocation: (location: Location) => void;
	setLocation: (location: string) => void;
	setStatus: (status: 'default' | 'loading' | 'valid' | 'invalid') => void;
	setSearchResult: (searchResult: LocationResult[] | null) => void;
	setSelectedTag: (selectedTag: string) => void;
	setSelectedPhysicalTag: (selectedPhysicalTag: string) => void;
	setZipCode: (zipCode: string) => void;
	setApptType: (apptType: string) => void;
	setSelectedDate: (date: string) => void;
	setOfferDateTimeUTC: (offerDateTimeUTC: string) => void;
	setSelectedFilter: (filter: string) => void;
	setOpenNow: (isOpenNow: boolean) => void;
	setErrors: (errors: { errorTitle: string; error: string; errorLink: string }[]) => void;
	clearErrors: () => void;
	setIsNextDateDisabled: (disabled: boolean) => void;
	incrementSearchCounter: () => void;
	setAccountIdError: (error: boolean) => void;
	setLocationSlots: (slots: Record<string, SlotType[]>) => void;
}

export const useLocationStore = create<LocationState>()(
	persist(
		(set) => ({
			location: '',
			status: 'default' as 'default' | 'loading' | 'valid' | 'invalid',
			searchResult: null,
			selectedTag: '',
			selectedPhysicalTag: '',
			zipCode: '',
			apptType: '',
			selectedDate: '',
			offerDateTimeUTC: new Date().toISOString(),
			selectedLocation: undefined,
			isWalkIn: false,
			selectedFilter: 'closest',
			isOpenNow: false,
			errors: [],
			isNextDateDisabled: false,
			searchCounter: 0,
			accountIdError: false,
			timezone: '',
			locationSlots: {},
			setTimezone: (timezone) => set(() => ({ timezone })),
			setIsWalkin: (isWalkIn) => set(() => ({ isWalkIn })),
			setSelectedLocation: (location) =>
				set(() => ({
					selectedLocation: {
						name: location.name,
						office: location.office,
						phone: location.phone,
						distance: location.distance,
						address: location.address,
						offerDateTimeUTC: location.offerDateTimeUTC,
						siteId: location.siteId,
						siteName: location.siteName,
						zip: location.zip,
						timezone: location.timezone,
						brand: location.brand,
						originalBrand: location.originalBrand,
						revspringAccountId: location.revspringAccountId,
						revspringLocationValue: location.revspringLocationValue,
						providerName: location.providerName,
						currentBrand: location.currentBrand,
					},
				})),
			setLocation: (location) => set(() => ({ location })),
			setStatus: (status) => set(() => ({ status })),
			setSearchResult: (searchResult) => set(() => ({ searchResult })),
			setSelectedTag: (selectedTag) => set(() => ({ selectedTag })),
			setSelectedPhysicalTag: (selectedPhysicalTag) => set(() => ({ selectedPhysicalTag })),
			setZipCode: (zipCode) => set(() => ({ zipCode })),
			setApptType: (apptType) => set(() => ({ apptType })),
			setSelectedDate: (date) => set(() => ({ selectedDate: date })),
			setOfferDateTimeUTC: (offerDateTimeUTC) => set(() => ({ offerDateTimeUTC })),
			setSelectedFilter: (filter) => set(() => ({ selectedFilter: filter })),
			setOpenNow: (isOpenNow) => set(() => ({ isOpenNow })),
			setErrors: (errors) => set(() => ({ errors })),
			clearErrors: () => set(() => ({ errors: [] })),
			setIsNextDateDisabled: (disabled) => set(() => ({ isNextDateDisabled: disabled })),
			incrementSearchCounter: () =>
				set((state) => ({
					searchCounter: state.searchCounter + 1,
				})),
			setAccountIdError: (error) => set(() => ({ accountIdError: error })),
			setLocationSlots: (slots) => set(() => ({ locationSlots: slots })),
		}),
		{
			name: 'location-storage',
			partialize: (state) => ({
				selectedLocation: state.selectedLocation,
				location: state.location,
				status: state.status,
				searchResult: state.searchResult,
				selectedTag: state.selectedTag,
				zipCode: state.zipCode,
				apptType: state.apptType,
				selectedDate: state.selectedDate,
				selectedFilter: state.selectedFilter,
				isOpenNow: state.isOpenNow,
				errors: state.errors,
			}),
		}
	)
);
